export default {
  label: "Venmo Transactions",
  script: `import { createSession } from '@plutoxyz/automation';
import { chromium } from 'playwright-core';

class PlutoPageLogic {
  plutoContext;
  constructor(plutoContext) {
    this.plutoContext = plutoContext;
  }
  getCurrentPage() {
    const pages = this.plutoContext.pages();
    if (pages.length === 0) {
      throw new Error("No pages found in the browser context.");
    }
    return pages[pages.length - 1];
  }
  async exists(locator, timeout = 1e3, interval = 100) {
    const attempts = timeout / interval;
    for (let i = 0; i < attempts; i++) {
      try {
        const handles = await locator.elementHandles();
        if (handles.length > 0) return true;
      } catch (e) {
      }
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    }
    return false;
  }
  async waitFor(locator, timeout = 3e4) {
    if (typeof locator === "string") {
      locator = this.getCurrentPage().locator(locator);
    }
    if (await this.exists(locator, timeout, 1e3)) {
      return locator;
    }
    return null;
  }
  async loadAndGet(url, locator, timeout = 6e4) {
    const page = this.getCurrentPage();
    await page.goto(url, { waitUntil: "domcontentloaded" });
    return this.waitFor(locator, timeout);
  }
}
function createPlutoPage(context) {
  const logic = new PlutoPageLogic(context);
  return new Proxy(logic, {
    get(target, prop) {
      if (prop in target) {
        return target[prop];
      }
      const currentPage = target.getCurrentPage();
      const value = currentPage[prop];
      if (typeof value === "function") {
        return value.bind(currentPage);
      }
      return value;
    }
  });
}

const session = await createSession();
const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = createPlutoPage(context);
await page.goto("https://venmo.com", { waitUntil: "domcontentloaded" });
if (await page.exists(page.getByRole("link", { name: "Log in", exact: true }))) {
  const emailInput = await page.loadAndGet(
    "https://id.venmo.com/signin",
    page.getByRole("textbox", { name: "email" })
  );
  const input = await session.prompt({
    title: "Login to Venmo",
    description: "Enter your Venmo credentials to prove your transactions",
    icon: "venmo.com",
    prompts: [
      {
        label: "Email or Phone Number",
        type: "text",
        attributes: {
          min_length: 3,
          max_length: 3,
          placeholder: "Email or Phone Number"
        }
      },
      {
        label: "Password",
        type: "password",
        attributes: {
          placeholder: "Password"
        }
      }
    ]
  });
  await emailInput?.fill(input[0]);
  await page.getByRole("button", { name: "Next" }).click();
  await page.getByRole("textbox", { name: "Password" }).fill(input[1]);
  await page.getByRole("button", { name: "Log in" }).click();
  await page.waitForSelector("h1", { timeout: 1e4 });
  const security = await page.getByRole("heading", { name: "Confirm your identity" }).isVisible().catch(() => false);
  if (security) {
    await page.waitForTimeout(2e3);
    await page.getByRole("button", { name: "Send code" }).click();
    const otp = await session.prompt({
      title: "Enter one time code",
      description: "Enter the one time code sent to your phone",
      icon: "venmo.com",
      prompts: [
        {
          label: "Code",
          type: "number_code",
          attributes: {
            length: 6
          }
        }
      ]
    });
    await page.fill("#confirm-input", otp[0]);
    await page.getByRole("button", { name: "Confirm it" }).click();
  }
}
await page.waitForSelector("#feed-tab-0, #feed-tab-1", { timeout: 1e4 });
await page.click("#feed-tab-1");
await page.waitForSelector('[data-testid="me-feed-container"]', {
  timeout: 1e4
});
await page.waitForTimeout(5e3);
const transactions = await page.evaluate(() => {
  const transactionData = [];
  const articles = document.querySelectorAll(
    '[data-testid="me-feed-container"] article'
  );
  articles.forEach((article) => {
    const headlineElement = article.querySelector('[id^="storyHeadlineId-"]');
    let contentElement = null;
    const timeElement = article.querySelector('div[aria-label*="ago"]');
    if (timeElement && timeElement.parentElement) {
      const parentContainer = timeElement.parentElement.parentElement;
      if (parentContainer) {
        contentElement = parentContainer.nextElementSibling?.querySelector("div");
      }
    }
    const allTopLevelDivs = article.querySelectorAll(":scope > div");
    const amountElement = allTopLevelDivs[allTopLevelDivs.length - 1];
    if (!headlineElement || !amountElement) return;
    const headline = headlineElement.textContent?.trim() || "";
    const content = contentElement?.textContent?.trim() || "";
    const amount = amountElement.textContent?.trim() || "";
    const timestamp = timeElement ? timeElement.getAttribute("aria-label") || timeElement.textContent?.trim() : "";
    let type = "unknown";
    if (amount.startsWith("+")) {
      type = "received";
    } else if (amount.startsWith("-")) {
      type = "sent";
    } else if (headline.includes("Transfer")) {
      type = "transfer";
    }
    if (headline.includes("You paid")) {
      headline.replace("You paid", "You sent money to");
    } else if (headline.includes("paid you")) {
      headline.replace("paid you", "sent money to you");
    }
    const cleanAmount = amount.replace(/^[+-]\\\s*/, "");
    const summary = headline + " - " + cleanAmount;
    transactionData.push({
      summary,
      type,
      amount,
      headline,
      content,
      timestamp
    });
  });
  return transactionData;
});
const recentTransactions = transactions.slice(0, 5);
if (recentTransactions.length === 0) {
  await session.prove("venmo_transactions", {
    transactions: [],
    multiple_transactions: []
  });
} else {
  const formattedTransactions = recentTransactions.map((tx) => tx.summary);
  const selectResult = await session.prompt({
    title: "Verify Venmo Transactions",
    description: "Select transaction(s) you want to verify",
    icon: "venmo.com",
    prompts: [
      {
        label: "Select Transactions",
        type: "checkbox",
        attributes: {
          options: formattedTransactions,
          multiple: true,
          min: 1,
          max: 5
        }
      }
    ]
  });
  const multipleSelection = selectResult[0] || [];
  const selectedMultipleTransactions = recentTransactions.filter(
    (tx) => multipleSelection.includes(tx.summary)
  );
  await session.prove("venmo_transactions", selectedMultipleTransactions);
}
await page.close();
await browser.close();
console.log("🎉 Session complete");
await session.close();
`,
};
